import React, { useState } from 'react';
import { Download, FileText, TrendingUp, Users, Truck } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { BarChart, PieChart, LineChart } from '../components/Charts';
import Breadcrumb from '../components/Breadcrumb';
import { formatCurrency, formatDate, exportToCSV, exportToJSON, calculatePercentage, groupByField } from '../utils/helpers';

export default function Reports() {
  const { state } = useApp();
  const [activeTab, setActiveTab] = useState('overview');

  const { prospects, spk, delivery, team } = state;

  // Aggregated data
  const totalRevenue = spk.reduce((s, i) => s + (i.harga || 0), 0);
  const cashSales = spk.filter(s => s.metodePembayaran === 'Cash');
  const kreditSales = spk.filter(s => s.metodePembayaran === 'Kredit');

  const statusGrouped = groupByField(prospects, 'status');
  const sourceGrouped = groupByField(prospects, 'sumberLead');
  const carGrouped = groupByField(spk, 'tipeMobil');

  const paymentPieData = [
    { label: 'Cash', value: cashSales.length, color: '#10B981' },
    { label: 'Kredit', value: kreditSales.length, color: '#6366F1' },
  ];

  const statusPieData = [
    { label: 'Hot', value: (statusGrouped.Hot || []).length, color: '#DC2626' },
    { label: 'Warm', value: (statusGrouped.Warm || []).length, color: '#EA580C' },
    { label: 'Cold', value: (statusGrouped.Cold || []).length, color: '#3B82F6' },
  ];

  const carBarData = Object.entries(carGrouped)
    .map(([label, arr]) => ({ label: label.replace('Honda ', ''), value: arr.length }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  const sourceBarData = Object.entries(sourceGrouped)
    .map(([label, arr]) => ({ label, value: arr.length }))
    .sort((a, b) => b.value - a.value);

  const tabs = [
    { key: 'overview', label: 'Overview', icon: TrendingUp },
    { key: 'prospects', label: 'Prospek', icon: Users },
    { key: 'sales', label: 'Penjualan', icon: FileText },
    { key: 'delivery', label: 'Delivery', icon: Truck },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Laporan' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Laporan & Analitik</h1>
          <p className="text-sm text-gray-500">Ringkasan performa penjualan</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportToCSV([...prospects, ...spk], 'laporan-honda')} className="btn-secondary"><Download size={16}/>CSV</button>
          <button onClick={() => exportToJSON({ prospects, spk, delivery, team }, 'laporan-honda')} className="btn-secondary"><Download size={16}/>JSON</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-100 pb-0">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${activeTab === key ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Icon size={15} />{label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Prospek', value: prospects.length, color: 'text-blue-600' },
              { label: 'Total SPK', value: spk.length, color: 'text-green-600' },
              { label: 'Total Revenue', value: `Rp ${(totalRevenue/1000000).toFixed(0)}M`, color: 'text-red-600' },
              { label: 'Terkirim', value: delivery.filter(d=>d.statusPengiriman==='Terkirim').length, color: 'text-purple-600' },
            ].map((s, i) => (
              <div key={i} className="card text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
                <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Distribusi Status Prospek</h3>
              <PieChart data={statusPieData} />
            </div>
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Metode Pembayaran</h3>
              <PieChart data={paymentPieData} />
            </div>
          </div>
        </div>
      )}

      {/* Prospects Tab */}
      {activeTab === 'prospects' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Sumber Lead</h3>
              <BarChart data={sourceBarData} height={200} />
            </div>
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Status Prospek</h3>
              <PieChart data={statusPieData} />
            </div>
          </div>
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Detail Sumber Lead</h3>
            <div className="space-y-2">
              {Object.entries(sourceGrouped).sort((a,b)=>b[1].length-a[1].length).map(([source, arr]) => (
                <div key={source} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">{source}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-24 h-1.5 bg-gray-200 rounded-full">
                      <div className="h-1.5 bg-red-500 rounded-full" style={{ width: `${calculatePercentage(arr.length, prospects.length)}%` }} />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 w-6">{arr.length}</span>
                    <span className="text-xs text-gray-400 w-10">({calculatePercentage(arr.length, prospects.length)}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sales Tab */}
      {activeTab === 'sales' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Penjualan per Model</h3>
              <BarChart data={carBarData} height={200} />
            </div>
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Metode Pembayaran</h3>
              <PieChart data={paymentPieData} />
            </div>
          </div>
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Daftar SPK Terbaru</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-gray-50"><th className="table-header">No. SPK</th><th className="table-header">Nama</th><th className="table-header">Tipe Mobil</th><th className="table-header">Harga</th><th className="table-header">Pembayaran</th><th className="table-header">Status</th></tr></thead>
                <tbody>
                  {spk.slice(0, 10).map(s => (
                    <tr key={s.id} className="table-row">
                      <td className="table-cell"><span className="font-mono text-xs text-red-600">{s.spkNumber}</span></td>
                      <td className="table-cell">{s.namaPembeli}</td>
                      <td className="table-cell text-sm">{s.tipeMobil}</td>
                      <td className="table-cell font-semibold">{formatCurrency(s.harga)}</td>
                      <td className="table-cell">{s.metodePembayaran}</td>
                      <td className="table-cell">{s.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Tab */}
      {activeTab === 'delivery' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="card text-center">
              <p className="text-xs text-gray-500">Total Delivery</p>
              <p className="text-3xl font-bold text-gray-700 mt-1">{delivery.length}</p>
            </div>
            <div className="card text-center">
              <p className="text-xs text-gray-500">Terkirim</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{delivery.filter(d=>d.statusPengiriman==='Terkirim').length}</p>
            </div>
          </div>
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Riwayat Delivery</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-gray-50"><th className="table-header">Nama</th><th className="table-header">Tipe Mobil</th><th className="table-header">No. Polisi</th><th className="table-header">Tgl Kirim</th><th className="table-header">Status</th></tr></thead>
                <tbody>
                  {delivery.map(d => (
                    <tr key={d.id} className="table-row">
                      <td className="table-cell">{d.namaPembeli}</td>
                      <td className="table-cell">{d.tipeMobil}</td>
                      <td className="table-cell"><span className="font-mono text-xs">{d.noPolisi || '-'}</span></td>
                      <td className="table-cell text-xs text-gray-500">{formatDate(d.tanggalKirim)}</td>
                      <td className="table-cell"><span className={`badge ${d.statusPengiriman === 'Terkirim' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{d.statusPengiriman}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
